/**
 * Call Button Component
 * Button to initiate a call from chat or client profile
 */

import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import { Phone, Video, Loader2 } from 'lucide-react';
import { callService } from '@/services/callService';
import { toast } from 'sonner';

export function CallButton({
  clientId,
  conversationId,
  onCallStarted,
  disabled = false,
  variant = 'default',
  size = 'default',
  showLabel = true,
  className
}) {
  const [loading, setLoading] = useState(false);
  const [callConfig, setCallConfig] = useState(null);

  // Check if calling is enabled
  React.useEffect(() => {
    const checkConfig = async () => {
      try {
        const config = await callService.getConfig();
        setCallConfig(config);
      } catch (e) {
        console.log('Call config not available');
      }
    };
    checkConfig();
  }, []);

  const handleStartCall = async (callType) => {
    if (loading) return;
    
    setLoading(true);
    try {
      const result = await callService.initiateCall({
        clientId,
        conversationId,
        callType
      });

      if (result.success) {
        toast.success('Call started');
        onCallStarted?.(result);
      } else {
        throw new Error('Failed to start call');
      }
    } catch (error) {
      console.error('Failed to start call:', error);
      toast.error(error.message || 'Failed to start call');
    } finally {
      setLoading(false);
    }
  };

  // Don't show if calling is not configured
  if (!callConfig?.enabled) {
    return null;
  }

  if (loading) {
    return (
      <Button variant={variant} size={size} disabled className={className}>
        <Loader2 className="h-4 w-4 animate-spin" />
        {showLabel && <span className="ml-2">Calling...</span>}
      </Button>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant={variant} 
          size={size} 
          disabled={disabled}
          className={className}
          data-testid="call-button"
        >
          <Phone className="h-4 w-4" />
          {showLabel && <span className="ml-2">Call</span>}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem 
          onClick={() => handleStartCall('video')}
          data-testid="video-call-option"
        >
          <Video className="h-4 w-4 mr-2" />
          Video Call
        </DropdownMenuItem>
        <DropdownMenuItem 
          onClick={() => handleStartCall('audio')}
          data-testid="audio-call-option"
        >
          <Phone className="h-4 w-4 mr-2" />
          Audio Call
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default CallButton;
